/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Download, Smartphone, Share, Plus, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}
function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent);
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

// Captures the beforeinstallprompt event globally so the button can use it later
let cachedDeferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    cachedDeferredPrompt = e;
  });
}

export default function InstallAppButton({ variant = "default", className = "" }) {
  const [installed, setInstalled] = useState(false);
  const [hasPrompt, setHasPrompt] = useState(!!cachedDeferredPrompt);
  const [hintOpen, setHintOpen] = useState(false);

  useEffect(() => {
    setInstalled(isStandalone());
    const onPrompt = () => setHasPrompt(true);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    // Poll once in case the event already fired before mount
    if (cachedDeferredPrompt) setHasPrompt(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const ios = isIos();
  const android = isAndroid();

  const handleClick = async () => {
    if (cachedDeferredPrompt) {
      cachedDeferredPrompt.prompt();
      try { await cachedDeferredPrompt.userChoice; } catch { /* ignore */ }
      cachedDeferredPrompt = null;
      setHasPrompt(false);
      return;
    }
    // No native prompt → show manual instructions
    setHintOpen(true);
  };

  const btnClass = variant === "compact"
    ? "inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[#E05A3D]/30 bg-[#E05A3D]/10 text-[#C84B31] text-xs font-bold hover:bg-[#E05A3D] hover:text-white transition-colors"
    : "inline-flex items-center gap-2.5 px-4 py-3 rounded-xl bg-gradient-to-r from-[#E05A3D] to-[#C84B31] text-white font-bold text-sm shadow-lg shadow-[#E05A3D]/20 hover:shadow-xl hover:shadow-[#E05A3D]/30 transition-all";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-btn"
        className={`${btnClass} ${className}`}
        title="Installer Mazra3ti sur votre écran d'accueil"
      >
        <Download size={variant === "compact" ? 14 : 16} />
        Installer l'app
        {!hasPrompt && (ios || android) && (
          <span className="text-[10px] font-medium opacity-80">· Guide</span>
        )}
      </button>

      <Dialog open={hintOpen} onOpenChange={setHintOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="install-hint-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone size={20} className="text-[#E05A3D]" />
              Installer Mazra3ti sur votre téléphone
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-[#1C3A27]">
            {ios && (
              <div className="rounded-xl border border-[#E2E4DF] bg-[#FAFAF7] p-4">
                <p className="font-bold mb-3 flex items-center gap-2">📱 iPhone / iPad (Safari)</p>
                <ol className="space-y-2 list-decimal list-inside text-[#5C6B5C]">
                  <li>Appuyez sur l'icône <Share size={14} className="inline mx-1 text-[#1E6BBA]" /> <strong>Partager</strong> en bas de Safari</li>
                  <li>Faites défiler et choisissez <strong>« Sur l'écran d'accueil »</strong> <Plus size={14} className="inline mx-1" /></li>
                  <li>Appuyez sur <strong>Ajouter</strong> en haut à droite</li>
                </ol>
                <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  ⚠️ L'installation ne fonctionne que dans <strong>Safari</strong>, pas dans Chrome ou Firefox sur iPhone.
                </p>
              </div>
            )}
            {android && (
              <div className="rounded-xl border border-[#E2E4DF] bg-[#FAFAF7] p-4">
                <p className="font-bold mb-3 flex items-center gap-2">🤖 Android (Chrome)</p>
                <ol className="space-y-2 list-decimal list-inside text-[#5C6B5C]">
                  <li>Touchez le menu <strong>⋮</strong> en haut à droite de Chrome</li>
                  <li>Choisissez <strong>« Installer l'application »</strong> ou <strong>« Ajouter à l'écran d'accueil »</strong></li>
                  <li>Confirmez en touchant <strong>Installer</strong></li>
                </ol>
                <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  💡 Si vous ne voyez pas l'option, fermez Chrome, rouvrez le site et naviguez quelques secondes — Chrome détectera l'app.
                </p>
              </div>
            )}
            {!ios && !android && (
              <div className="rounded-xl border border-[#E2E4DF] bg-[#FAFAF7] p-4">
                <p className="font-bold mb-3">💻 Ordinateur (Chrome / Edge)</p>
                <ol className="space-y-2 list-decimal list-inside text-[#5C6B5C]">
                  <li>Cliquez sur l'icône <Download size={14} className="inline mx-1" /> dans la barre d'adresse</li>
                  <li>Ou ouvrez le menu <strong>⋮</strong> → <strong>« Installer Mazra3ti »</strong></li>
                  <li>Confirmez l'installation</li>
                </ol>
              </div>
            )}
            <p className="text-xs text-[#5C6B5C]">
              Une fois installée, Mazra3ti s'ouvre comme une vraie application, fonctionne hors-ligne et apparaît sur votre écran d'accueil.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
